import { supabase } from './supabase'
import type { User } from '../types/database'

export const auth = {
  // Email/Password signup
  async signUp(email: string, password: string, userData: Partial<User>) {
    try {
      // 1. Create auth user with metadata
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: userData.first_name || '',
            last_name: userData.last_name || '',
            interests: userData.interests || [],
          }
        }
      })

      if (authError) throw authError
      if (!authData.user) throw new Error('User creation failed')

      // 2. Update the user profile that was auto-created by trigger with additional data
      // The trigger creates a basic profile, we just need to update it with the form data
      const { error: updateError } = await supabase
        .from('users')
        .update({
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          interests: userData.interests || [],
          bio: userData.bio,
          location: userData.location,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          email_notifications: userData.email_notifications ?? true,
        })
        .eq('id', authData.user.id)
      
      if (updateError) {
        console.error('Profile update error:', updateError)
        // Don't throw - profile will exist from trigger, just might not have all fields
      }

      // Welcome email is sent by a DB trigger when onboarding completes (profile_completed)
      // This prevents duplicate emails and handles both email and social signups uniformly

      return { user: authData.user, session: authData.session }
    } catch (error) {
      console.error('Signup error:', error)
      throw error
    }
  },

  // Email/Password sign in
  async signIn(email: string, password: string) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) throw error
      return data
    } catch (error) {
      console.error('Sign-in error:', error)
      throw error
    }
  },

  // Social login - following official Supabase documentation
  async signInWithProvider(provider: 'google' | 'facebook' | 'apple') {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: window.location.origin, // Always redirect to current origin
        // Request name+email from Apple so we can pre-populate the profile
        // and avoid asking the user again (App Store Guideline 4 - Design).
        ...(provider === 'apple' ? { scopes: 'name email' } : {})
      }
    })

    if (error) throw error
    return data
  },

  // Create or update user profile after social login
  async handleSocialAuthCallback(authUser: any, additionalData?: Partial<User>) {
    try {
      // Check if user profile exists
      const { data: existingUser } = await supabase
        .from('users')
        .select('*')
        .eq('id', authUser.id)
        .maybeSingle()

      if (!existingUser) {
        // Extract name from auth metadata
        const metadata = authUser.user_metadata || {}
        const fullName = metadata.full_name || metadata.name || ''
        const [firstName = '', lastName = ''] = fullName.split(' ')

        // Create new user profile
        const { error: profileError } = await supabase
          .from('users')
          .insert({
            id: authUser.id,
            email: authUser.email!,
            first_name: additionalData?.first_name || metadata.first_name || firstName,
            last_name: additionalData?.last_name || metadata.last_name || lastName,
            interests: additionalData?.interests || [],
            bio: additionalData?.bio,
            location: additionalData?.location,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            is_verified: authUser.email_confirmed_at ? true : false,
            email_notifications: true,
          })

        if (profileError) throw profileError
        
        // Welcome email is sent by a DB trigger when onboarding completes (profile_completed)
      }

      return authUser
    } catch (error) {
      console.error('Error handling social auth callback:', error)
      throw error
    }
  },

  // Sign out
  async signOut() {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  },

  // Get current user
  async getCurrentUser() {
    const { data: { user } } = await supabase.auth.getUser()
    return user
  },

  // Get user profile from database
  async getUserProfile(userId: string): Promise<User | null> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) {
        // 406 error often means the table doesn't exist or RLS is blocking
        if (error.code === 'PGRST116') {
          // User not found is ok
          return null
        }
        console.error('Error fetching user profile:', error)
        return null
      }

      return data
    } catch (err) {
      console.error('Error in getUserProfile:', err)
      return null
    }
  },

  // Update user profile (for completing profile after social login)
  async updateUserProfile(userId: string, updates: Partial<User>) {
    const { data, error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', userId)
      .select()
      .single()

    if (error) throw error
    return data
  },

  // Password reset request
  async resetPassword(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    if (error) throw error
    return { success: true }
  },

  // Update password (after reset)
  async updatePassword(newPassword: string) {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      })

      if (error) throw error
      return { success: true }
    } catch (error) {
      console.error('Password update error:', error)
      throw error
    }
  },

  // Get session
  async getSession() {
    const { data: { session } } = await supabase.auth.getSession()
    return session
  },

  // Listen to auth state changes
  onAuthStateChange(callback: (event: any, session: any) => void) {
    return supabase.auth.onAuthStateChange(callback)
  },
}